
import { useCallback, useEffect, useMemo, useState } from "react";
import GeoOverviewTable from "./GeoOverviewTable";
import GeoOverviewChart from "./GeoOverviewChart";
import SinglePromptSimulationModal from "./SinglePromptSimulationModal";
import { FaArrowsLeftRight, FaArrowTrendDown, FaArrowTrendUp } from "react-icons/fa6";
import { useLanguage } from "../../../Translations/LenguageContext";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;
const PAGE_LIMIT = 10;

const MetricStatus = ({ label, value }) => {
    const getStatus = (value) => {
        if (value == null || Number.isNaN(Number(value))) {
            return {
                className: "simulation_metric_cell_warning",
                icon: <FaArrowsLeftRight />
            };
        }

        if (value < 4) {
            return {
                className: "simulation_metric_cell_negative",
                icon: <FaArrowTrendDown />
            };
        }

        if (value <= 6) {
            return {
                className: "simulation_metric_cell_warning",
                icon: <FaArrowsLeftRight />
            };
        }

        return {
            className: "simulation_metric_cell_positive",
            icon: <FaArrowTrendUp />
        };
    };

    const status = getStatus(value);

    return (
        <div className="geo_overview_header_statuses_cont">
            <p>{label}</p>
            <span
                className={`simulation_metric_cell_span ${status.className}`}
                title={value == null ? "No data" : `Average: ${value}`}
            >
                <i>{status.icon}</i>
            </span>
        </div>
    );
};

const OverviewMain = ({ account_id, org_id, geo_id }) => {
    const { t } = useLanguage()
    const [simulations, setSimulations] = useState([]);
    const [loadingSimulations, setLoadingSimulations] = useState(false);
    const [pagination, setPagination] = useState(null);
    const [engines, setEngines] = useState([]);
    const [simulationCount, setSimulationCount] = useState(0);
    const [selectedEngine, setSelectedEngine] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingChartData, setLoadingChartData] = useState(false)
    const [chartData, setChartData] = useState([])
    const [prompts, setPrompts] = useState([])
    const [selectedPromptId, setSelectedPromptId] = useState()
    const [selectedEnginesChart, setSelectedEnginesChart] = useState([])
    const [selectedMetricKey, setSelectedMetricKey] = useState()
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [openSinglePromptSimulationModal, setOpenSinglePromptSimulationModal] = useState(false)
    const [selectedSimulation, setSelectedSimulation] = useState({})
    const [averages, setAverages] = useState()
    const [url, setUrl] = useState(null)
    const [currentMetricIndex, setCurrentMetricIndex] = useState(0);

    const metrics = [
        { key: "visibility", label: t.geo.visibility },
        { key: "effectiveness", label: t.geo.effectiveness },
        { key: "position", label: t.geo.position },
        { key: "sentiment", label: t.geo.sentiment }
    ];

    const currentMetric = metrics[currentMetricIndex];
    const value = averages?.[currentMetric.key];
    const trend = value > 5 ? "up" : "down";

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });

    const fetchDataSimulations = useCallback(
        async (page = 1, limit = PAGE_LIMIT, engine = null) => {
            if (!account_id || !org_id || !geo_id) return;

            setLoadingSimulations(true);

            try {
                const response = await fetch(
                    `${GEN_BACKEND_LINK}/get_geo_prompt_simulations`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            account_id,
                            org_id,
                            geo_id,
                            page,
                            limit,
                            engine: engine || null,
                        }),
                    }
                );

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.error || "Failed to fetch simulations"
                    );
                }

                const fetchedEngines = Array.isArray(result.engines)
                    ? result.engines
                    : [];

                setSimulations(
                    Array.isArray(result.data) ? result.data : []
                );
                setPagination(result.pagination ?? null);
                setEngines(fetchedEngines);
                setSimulationCount(result.simulation_count ?? 0);
                setCurrentPage(result.pagination?.page ?? page);
                setPrompts(result?.prompts)
                setAverages(result?.averages)
                setUrl(result?.url)
                setSelectedEngine((previous) => {
                    if (engine) return engine;

                    return previous && fetchedEngines.includes(previous)
                        ? previous
                        : fetchedEngines[0] ?? "";
                });
            } catch (error) {
                console.error(
                    "Error fetching GEO prompt simulations:",
                    error
                );
                setSimulations([]);
                setPagination(null);
            } finally {
                setLoadingSimulations(false);
            }
        },
        [account_id, org_id, geo_id]
    );

    const fetchChartData = useCallback(async () => {
        if (
            !selectedPromptId ||
            !selectedEnginesChart ||
            !selectedMetricKey ||
            !fromDate ||
            !toDate ||
            fromDate > toDate
        ) {
            return;
        }

        setLoadingChartData(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_prompt_simulations_chart_data`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id,
                        prompt_id: selectedPromptId,
                        engines: selectedEnginesChart,
                        metric: selectedMetricKey,
                        from_date: fromDate,
                        to_date: toDate,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(
                    result.error || "Failed to fetch simulations"
                );
            }

            setChartData(
                Array.isArray(result.data) ? result.data : []
            );
        } catch (error) {
            console.error(
                "Error fetching GEO prompt simulations:",
                error
            );
            setChartData([]);
        } finally {
            setLoadingChartData(false);
        }
    }, [
        account_id,
        org_id,
        geo_id,
        selectedPromptId,
        selectedEnginesChart,
        selectedMetricKey,
        fromDate,
        toDate,
    ]);

    const filteredEngines = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        return engines.filter((engine) =>
            engine.toLowerCase().includes(query)
        );
    }, [engines, searchTerm]);

    const sortedSimulations = useMemo(() => {
        const rows = [...simulations];

        if (!sortConfig.key) return rows;

        const { key, direction } = sortConfig;

        rows.sort((a, b) => {
            const valueA = a[key];
            const valueB = b[key];

            if (valueA == null && valueB == null) return 0;
            if (valueA == null) return 1;
            if (valueB == null) return -1;

            const comparison =
                typeof valueA === "number" && typeof valueB === "number"
                    ? valueA - valueB
                    : String(valueA).localeCompare(
                        String(valueB),
                        undefined,
                        {
                            numeric: true,
                            sensitivity: "base",
                        }
                    );

            return direction === "asc" ? comparison : -comparison;
        });

        return rows;
    }, [simulations, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((previous) => ({
            key,
            direction:
                previous.key === key && previous.direction === "asc"
                    ? "desc"
                    : "asc",
        }));
    };

    const handleEngineChange = (engine) => {
        setSelectedEngine(engine);
        setCurrentPage(1);
        setSearchTerm("");

        fetchDataSimulations(1, pagination?.limit ?? PAGE_LIMIT, engine);
    };

    const handlePageChange = (page) => {
        if (loadingSimulations) return;

        const totalPages = pagination?.total_pages ?? 1;

        if (page < 1 || page > totalPages) return;

        setCurrentPage(page);

        fetchDataSimulations(
            page,
            pagination?.limit ?? PAGE_LIMIT,
            selectedEngine || null
        );
    };

    const handleOpenSinglePromptSimulation = (item) => {
        setSelectedSimulation(item)
        setOpenSinglePromptSimulationModal(true)
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentMetricIndex((prev) => (prev + 1) % metrics.length);
        }, 4000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        setSimulations([]);
        setPagination(null);
        setEngines([]);
        setSimulationCount(0);
        setSelectedEngine("");
        setCurrentPage(1);
        setSearchTerm("");
        setSortConfig({ key: null, direction: "asc" });

        if (account_id && org_id && geo_id) {
            fetchDataSimulations(1, PAGE_LIMIT, null);
        }
    }, [account_id, org_id, geo_id, fetchDataSimulations]);

    return (
        <div>
            <div className="geo_overview_header">
                <div className="geo_overview_header_inner">
                    <p
                        key={`${currentMetric.key}-${trend}`}
                        className="fade-in"
                    >
                        <strong>{currentMetric.label}</strong> {t.geo.is_trending} <strong>{t.geo[trend]}</strong>{" "}
                        <span
                            className={
                                trend === "down"
                                    ? "simulation_metric_cell_negative"
                                    : "simulation_metric_cell_positive"
                            }
                        >
                            {trend === "down" ? <i><FaArrowTrendDown /></i> : <i><FaArrowTrendUp /></i>}
                        </span>
                    </p>

                    <div className="geo_overview_header_statuses">
                        <MetricStatus
                            label={t.geo.effectiveness}
                            value={averages?.effectiveness}
                        />

                        <MetricStatus
                            label={t.geo.visibility}
                            value={averages?.visibility}
                        />

                        <MetricStatus
                            label={t.geo.sentiment}
                            value={averages?.sentiment}
                        />

                        <MetricStatus
                            label={t.geo.position}
                            value={averages?.position}
                        />
                    </div>
                </div>
            </div>

            <div className="geo_overview_res_cont">
                <GeoOverviewChart
                    data={chartData}
                    engines={engines}
                    prompts={prompts}
                    fetchChartData={fetchChartData}
                    loadingChartData={loadingChartData}
                    selectedPromptId={selectedPromptId}
                    setSelectedPromptId={setSelectedPromptId}
                    selectedEnginesChart={selectedEnginesChart}
                    setSelectedEnginesChart={setSelectedEnginesChart}
                    selectedMetricKey={selectedMetricKey}
                    setSelectedMetricKey={setSelectedMetricKey}
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                />

                {/* <div className="geo_overview_res_cont_rows">
                    <div className="geo_overview_res_cont_rows_header" >
                        Avarage
                    </div>

                    <div className="geo_overview_res_cont_rows_body">
                        {Array.from({ length: 7 }, (_, index) => (
                            <div
                                className="geo_overview_res_cont_row"
                                key={index}
                            />
                        ))}
                    </div>
                </div> */}
            </div>

            <GeoOverviewTable
                simulations={sortedSimulations}
                loadingSimulations={loadingSimulations}
                pagination={pagination}
                engines={filteredEngines}
                simulationCount={simulationCount}
                selectedEngine={selectedEngine}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                sortConfig={sortConfig}
                onSort={handleSort}
                onEngineChange={handleEngineChange}
                onPageChange={handlePageChange}
                handleOpenSinglePromptSimulation={handleOpenSinglePromptSimulation}
            />

            <SinglePromptSimulationModal
                account_id={account_id}
                org_id={org_id}
                geo_id={geo_id}
                selectedSimulation={selectedSimulation}
                setSelectedSimulation={setSelectedSimulation}
                show={openSinglePromptSimulationModal}
                handleClose={() => setOpenSinglePromptSimulationModal(false)}
            />
        </div>
    );
};

export default OverviewMain;
